/**
 * Viewport input → camera navigation and selection, Unreal-style (SPEC §4.6):
 *
 * - LMB click: select what's under the cursor (or clear), via `selection.set`;
 *   Ctrl toggles it in or out and Shift adds it, as in Unreal
 * - LMB drag: marquee select (Shift adds, Ctrl subtracts; see selection/marquee)
 * - RMB drag: look; RMB + WASD/QE: fly (Shift for faster); RMB + wheel: camera speed
 * - RMB click (no drag, no flight): select what's under the cursor and open
 *   its context menu, as in Unreal; over empty space, the viewport's menu
 * - Orthographic works the same, except the wheel zooms (see cameraMath)
 * - Alt + LMB drag: orbit around the focus point
 * - MMB drag: pan
 * - Wheel: dolly toward the focus point
 *
 * It's DOM-agnostic (the viewport panel forwards events), so the mapping is
 * unit-tested with plain objects. Camera motion is ephemeral view state (A-16);
 * selection and camera speed go through the Command Bus.
 */
import type { CommandRequest } from "../../commands/types";
import type { ReadableStore } from "../../core/store";
import type { EntityId } from "../../world/entity";
import type { SelectionState } from "../selection/Selection";
import { marqueeRect } from "../selection/marquee";
import type { MarqueeMode, Rect } from "../selection/marquee";
import { clickMode, selectionAfterClick } from "../selection/selectionModel";
import type { ModifierKeys } from "../selection/selectionModel";
import type { SettingsValues } from "../settings/EditorSettings";
import { dolly, FAST_FLY_MULTIPLIER, fly, look, orbit, pan } from "./cameraMath";
import type { FlyInput } from "./cameraMath";
import type { ViewportCamera } from "./ViewportCamera";

export interface PointerLike extends ModifierKeys {
  readonly button: number;
  readonly clientX: number;
  readonly clientY: number;
  readonly altKey: boolean;
}

export interface RectLike {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface ViewportNavigatorOptions {
  readonly camera: ViewportCamera;
  readonly settings: ReadableStore<SettingsValues>;
  readonly dispatch: (request: CommandRequest) => unknown;
  /** The entity under a viewport point (CSS px from the viewport's top-left). */
  readonly pick: (x: number, y: number) => Promise<EntityId | null>;
  /** The current selection, which Ctrl/Shift clicks change rather than replace. */
  readonly selection: ReadableStore<SelectionState>;
  /** Keeps the frame loop running while flying; returns a release function. */
  readonly requestAnimation: () => () => void;
  /** Called on any camera navigation (the nav hint hides after the first one). */
  readonly onNavigated: () => void;
  /** An RMB click on an entity (now selected): open its context menu at this page point. */
  readonly onContextClick: (at: { x: number; y: number }) => void;
  /** An RMB click on empty space: open the viewport's own menu (Reset view) at this page point. */
  readonly onViewMenu: (at: { x: number; y: number }) => void;
  /** The marquee while an LMB drag draws it (viewport pixels), or null when it ends. */
  readonly onMarquee: (rect: Rect | null) => void;
  /** A finished marquee, and whether it replaces, adds to or subtracts from the selection. */
  readonly onMarqueeEnd: (rect: Rect, mode: MarqueeMode) => void;
}

type Mode = "idle" | "click" | "marquee" | "look" | "orbit" | "pan";

/** A press that moves farther than this is a drag, not a click. */
const CLICK_SLOP_PX = 4;
const MOUSE = { left: 0, middle: 1, right: 2 } as const;
const FLY_KEYS: Readonly<Record<string, keyof FlyInput | `-${keyof FlyInput}`>> = {
  w: "forward",
  s: "-forward",
  d: "right",
  a: "-right",
  e: "up",
  q: "-up",
};

export class ViewportNavigator {
  private mode: Mode = "idle";
  private last = { x: 0, y: 0 };
  /** Where the press happened, in viewport pixels (a marquee's fixed corner). */
  private start = { x: 0, y: 0 };
  /** The viewport's page offset at the press, to convert pointer positions. */
  private origin = { left: 0, top: 0 };
  private dragged = 0;
  private button = -1;
  /** WASD/QE moved the camera during this RMB press, so its release isn't a click. */
  private flew = false;
  private readonly held = new Set<string>();
  private releaseAnimation: (() => void) | null = null;

  constructor(private readonly options: ViewportNavigatorOptions) {}

  /** True while a mouse button drives the camera (the page should not scroll or select text). */
  get isNavigating(): boolean {
    return this.mode === "look" || this.mode === "orbit" || this.mode === "pan";
  }

  /** True while RMB is held: WASD/QE fly instead of switching tools. */
  get isFlying(): boolean {
    return this.mode === "look";
  }

  pointerDown(event: PointerLike, rect: RectLike): void {
    this.last = { x: event.clientX, y: event.clientY };
    this.origin = { left: rect.left, top: rect.top };
    this.start = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    this.dragged = 0;
    this.flew = false;
    this.button = event.button;
    this.mode = modeFor(event);
    if (this.mode === "look") this.releaseAnimation = this.options.requestAnimation();
  }

  pointerMove(event: PointerLike): void {
    const dx = event.clientX - this.last.x;
    const dy = event.clientY - this.last.y;
    this.last = { x: event.clientX, y: event.clientY };
    this.dragged += Math.abs(dx) + Math.abs(dy);

    const { camera } = this.options;
    switch (this.mode) {
      case "look":
        this.navigate(() => {
          camera.navigate((c) => look(c, dx, dy));
        });
        break;
      case "orbit":
        this.navigate(() => {
          camera.navigate((c) => orbit(c, dx, dy));
        });
        break;
      case "pan":
        this.navigate(() => {
          camera.navigate((c) => pan(c, dx, dy, camera.viewportSize.height));
        });
        break;
      case "click":
        // Past the click tolerance, a left press becomes a marquee.
        if (this.dragged > CLICK_SLOP_PX) {
          this.mode = "marquee";
          this.options.onMarquee(this.marqueeTo(event));
        }
        break;
      case "marquee":
        this.options.onMarquee(this.marqueeTo(event));
        break;
      case "idle":
        break;
    }
  }

  /**
   * Ends the current gesture. An LMB click (no drag) picks and selects; an
   * RMB click on an entity selects it and asks for its context menu.
   */
  async pointerUp(event: PointerLike, rect: RectLike): Promise<void> {
    const still = this.dragged <= CLICK_SLOP_PX;
    const wasClick = this.mode === "click" && still;
    const wasContextClick = this.button === MOUSE.right && still && !this.flew;
    const marquee = this.mode === "marquee" ? this.marqueeTo(event) : null;
    this.endGesture();
    if (marquee) {
      this.options.onMarquee(null);
      this.options.onMarqueeEnd(marquee, marqueeMode(event));
      return;
    }
    if (wasContextClick) {
      await this.contextClick(event, rect);
      return;
    }
    if (!wasClick) return;

    const hit = await this.options.pick(event.clientX - rect.left, event.clientY - rect.top);
    const mode = clickMode(event, "add");
    if (mode === "replace") {
      this.options.dispatch({
        type: "selection.set",
        payload: { ids: hit ? [hit] : [] },
        origin: "viewport",
      });
      return;
    }
    // A modified click on empty space leaves the selection as it is.
    if (!hit) return;
    const next = selectionAfterClick(this.options.selection.getSnapshot(), hit, { mode });
    this.options.dispatch({
      type: "selection.set",
      payload: { ids: next.ids, ...(next.active !== null && { active: next.active }) },
      origin: "viewport",
    });
  }

  /** Wheel: dolly, or (while RMB is held) change the camera speed like Unreal. */
  wheel(deltaY: number): void {
    const direction = Math.sign(-deltaY);
    if (direction === 0) return;

    if (this.mode === "look") {
      const speed = this.options.settings.getSnapshot().cameraSpeed;
      const next = Math.max(1, Math.min(8, speed + direction));
      if (next !== speed) {
        this.options.dispatch({
          type: "editor.setSetting",
          payload: { key: "cameraSpeed", value: next },
          origin: "viewport",
        });
      }
      return;
    }

    this.navigate(() => {
      this.options.camera.navigate((c) => dolly(c, direction));
    });
  }

  keyDown(key: string): void {
    const lower = key.toLowerCase();
    this.held.add(lower);
    if (this.mode === "look" && lower in FLY_KEYS) this.flew = true;
  }

  keyUp(key: string): void {
    this.held.delete(key.toLowerCase());
  }

  /** Clears held keys, e.g. when the window loses focus mid-flight. */
  releaseAll(): void {
    this.held.clear();
    this.endGesture();
  }

  /** Per-frame flight (the frame loop's input phase). */
  tick(dt: number): void {
    if (this.mode !== "look") return;
    const input = this.flyInput();
    if (input.forward === 0 && input.right === 0 && input.up === 0) return;

    const speed = this.options.settings.getSnapshot().cameraSpeed;
    const boost = this.held.has("shift") ? FAST_FLY_MULTIPLIER : 1;
    this.navigate(() => {
      this.options.camera.navigate((c) => fly(c, input, dt, speed, boost));
    });
  }

  private flyInput(): FlyInput {
    const input = { forward: 0, right: 0, up: 0 };
    for (const key of this.held) {
      const axis = FLY_KEYS[key];
      if (!axis) continue;
      if (axis.startsWith("-")) input[axis.slice(1) as keyof FlyInput] -= 1;
      else input[axis as keyof FlyInput] += 1;
    }
    return input;
  }

  private marqueeTo(event: PointerLike): Rect {
    return marqueeRect(this.start, {
      x: event.clientX - this.origin.left,
      y: event.clientY - this.origin.top,
    });
  }

  /** On an entity, its menu; over empty space, the viewport's own (as Unreal opens one there too). */
  private async contextClick(event: PointerLike, rect: RectLike): Promise<void> {
    const hit = await this.options.pick(event.clientX - rect.left, event.clientY - rect.top);
    if (!hit) {
      this.options.onViewMenu({ x: event.clientX, y: event.clientY });
      return;
    }
    // On a selected entity the menu acts on the whole selection, so keep it (Unreal).
    if (!this.options.selection.getSnapshot().ids.includes(hit)) {
      this.options.dispatch({ type: "selection.set", payload: { ids: [hit] }, origin: "viewport" });
    }
    this.options.onContextClick({ x: event.clientX, y: event.clientY });
  }

  private navigate(move: () => void): void {
    move();
    this.options.onNavigated();
  }

  private endGesture(): void {
    this.mode = "idle";
    this.releaseAnimation?.();
    this.releaseAnimation = null;
  }
}

/** Shift adds and Ctrl (Cmd) subtracts, as in Blender's box select. */
function marqueeMode(keys: ModifierKeys): MarqueeMode {
  if (keys.ctrlKey || keys.metaKey) return "subtract";
  return keys.shiftKey ? "add" : "replace";
}

/** Which gesture a press starts. */
function modeFor(event: PointerLike): Mode {
  if (event.button === MOUSE.right) return "look";
  if (event.button === MOUSE.middle) return "pan";
  if (event.button === MOUSE.left) return event.altKey ? "orbit" : "click";
  return "idle";
}
